import { computed, inject, Injectable, signal } from "@angular/core";
import { FindStorePosProductResponseDto } from "../../../core/stores/dtos/partials/find-store-pos-product.response.dto";
import { StoresApiService } from "../../../core/stores/stores-api.service";
import { FindStorePosSettingsResponseDto } from "../../../core/stores/dtos/find-store-pos-settings.response.dto";
import { FindStorePosCategoryResponseDto } from "../../../core/stores/dtos/partials/find-store-pos-category.response.dto";

@Injectable({
  providedIn: 'root',
})
export class PosCatalogService {
  readonly #api = inject(StoresApiService);

  readonly loading = signal(false);

  readonly #settings = signal<FindStorePosSettingsResponseDto | null>(null);
  readonly #categories = signal<FindStorePosCategoryResponseDto[]>([]);
  readonly #products = signal<FindStorePosProductResponseDto[]>([]);

  readonly settings = this.#settings.asReadonly();
  readonly categories = this.#categories.asReadonly();
  readonly products = this.#products.asReadonly();

  readonly categoriesById = computed(() => {
    const map = new Map<number, FindStorePosCategoryResponseDto>();

    for (const category of this.#categories()) {
      map.set(category.id, category);
    }

    return map;
  });

  readonly productsById = computed(() => {
    const map = new Map<number, FindStorePosProductResponseDto>();

    for (const product of this.#products()) {
      map.set(product.id, product);
    }

    return map;
  });

  async init(storeId: number): Promise<void> {
    try {
      this.loading.set(true);

      const { settings, products, categories } =
        await this.#api.findStorePos(storeId, {
          page: 1,
          size: 1_000_000, // TODO: Replace with proper pagination/sync
        });

      this.#settings.set(settings);
      this.#categories.set(categories);
      this.#products.set(products.data);
    } catch (error) {
      console.error('[POS] Error loading catalog:', error);
      throw error;
    } finally {
      this.loading.set(false);
    }
  }

  getProduct(productId: number): FindStorePosProductResponseDto | undefined {
    return this.productsById().get(productId);
  }

  getCategory(
    categoryId: number,
  ): FindStorePosCategoryResponseDto | undefined {
    return this.categoriesById().get(categoryId);
  }

  clear(): void {
    this.#settings.set(null);
    this.#categories.set([]);
    this.#products.set([]);
  }
}